
/*
 * TypeFase — Universal Linked Cart Groups
 * Compatible with Minimog 6.0
 *
 * Supports:
 * - One main product with multiple add-ons
 * - Unique group IDs
 * - Per-set, per-unit, and one-time add-ons
 * - Linked quantity changes
 * - Linked removal
 * - Legacy Custom Strap Design add-ons
 *
 * This script does NOT hide cart rows or change
 * displayed prices. Those changes come later.
 */

(() => {
  'use strict';

  const LEGACY_STRAP_VARIANT = '48845367607501';

  let updating = false;
  let checking = false;

  const routeRoot = () =>
    window.Shopify?.routes?.root || '/';

  const cartUrl = path =>
    routeRoot().replace(/\/?$/, '/') + path;

  /* ------------------------------------------
     SHOPIFY REQUESTS
  ------------------------------------------ */

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options
    });

    const result = await response
      .json()
      .catch(() => ({}));

    if (!response.ok || result.errors) {
      throw new Error(
        typeof result.errors === 'string'
          ? result.errors
          : result.description ||
            result.message ||
            'Unable to update the cart.'
      );
    }

    return result;
  }

  async function getCart() {
    return requestJSON(cartUrl('cart.js'));
  }

  /* ------------------------------------------
     LINE ITEM METADATA
  ------------------------------------------ */

  function properties(item) {
    return item?.properties || {};
  }

  function groupId(item) {
    return String(
      properties(item)._typefase_group || ''
    ).trim();
  }

  function role(item) {
    return String(
      properties(item)._typefase_role || ''
    ).trim().toLowerCase();
  }

  function isMain(item) {
    return role(item) === 'main';
  }

  function isAddon(item) {
    return role(item) === 'addon';
  }

  function addonType(item) {
    const declared =
      properties(item)._typefase_addon_type;

    if (declared) {
      return String(declared);
    }

    // Compatibility with the original
    // Custom Strap Design integration.
    if (
      String(item.variant_id || item.id) ===
      LEGACY_STRAP_VARIANT
    ) {
      return 'custom_strap';
    }

    return '';
  }

  function quantityMode(item) {
    const declared =
      properties(item)._typefase_qty_mode;

    if (declared) {
      return String(declared)
        .trim()
        .toLowerCase();
    }

    // Existing strap orders are charged
    // once for every main product set.
    if (
      addonType(item) === 'custom_strap'
    ) {
      return 'per_set';
    }

    return '';
  }

  function multiplier(item) {
    const raw =
      properties(item)._typefase_qty_multiplier;

    if (raw === undefined || raw === null ||
        raw === '') {
      return 1;
    }

    const value = Number(raw);

    return Number.isSafeInteger(value) &&
      value > 0
      ? value
      : null;
  }

  /* ------------------------------------------
     FIND AND VALIDATE A GROUP
  ------------------------------------------ */

  function findGroup(cart, line) {
    const selected =
      cart.items[Number(line) - 1];

    if (!selected) {
      return null;
    }

    const id = groupId(selected);

    if (!id || !isMain(selected)) {
      return null;
    }

    const members = cart.items.filter(item =>
      groupId(item) === id
    );

    const mains = members.filter(isMain);
    const addons = members.filter(isAddon);

    // Never guess how to handle a group
    // containing multiple main products or
    // unknown roles.
    if (
      mains.length !== 1 ||
      mains[0].key !== selected.key ||
      members.length !==
        mains.length + addons.length
    ) {
      return null;
    }

    const types = new Set();

    for (const addon of addons) {
      const type = addonType(addon);
      const mode = quantityMode(addon);
      const factor = multiplier(addon);

      if (
        !type ||
        !['per_set', 'per_unit', 'once']
          .includes(mode) ||
        factor === null
      ) {
        return null;
      }

      // Each add-on type should have a
      // unique identity within a group.
      if (types.has(type)) {
        return null;
      }

      types.add(type);
    }

    return {
      id,
      main: mains[0],
      addons
    };
  }

  /* ------------------------------------------
     ADD-ON QUANTITY RULES
  ------------------------------------------ */

  function targetQuantity(addon, mainQty) {
    if (mainQty === 0) {
      return 0;
    }

    const mode = quantityMode(addon);
    const factor = multiplier(addon);

    if (mode === 'once') {
      return factor;
    }

    // per_set: multiplier per main set
    // per_unit: multiplier per main unit
    // The product integration determines
    // what one main cart unit represents.
    if (
      mode === 'per_set' ||
      mode === 'per_unit'
    ) {
      return mainQty * factor;
    }

    throw new Error(
      'Unsupported add-on quantity rule.'
    );
  }

  /* ------------------------------------------
     UPDATE AN ENTIRE GROUP
  ------------------------------------------ */

  async function updateGroup(
    instance,
    group,
    quantity
  ) {
    if (updating) {
      return;
    }

    updating = true;

    try {
      instance.loading?.start();

      const updates = {};

      updates[group.main.key] = quantity;

      for (const addon of group.addons) {
        updates[addon.key] =
          targetQuantity(addon, quantity);
      }

      const response = await requestJSON(
        cartUrl('cart/update.js'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With':
              'XMLHttpRequest'
          },
          body: JSON.stringify({
            updates
          })
        }
      );

      // Confirm the server accepted all
      // quantities before displaying success.
      for (const [key, expected] of
        Object.entries(updates)) {
        const matching =
          response.items.find(item =>
            item.key === key
          );

        const actual =
          matching?.quantity || 0;

        if (actual !== expected) {
          throw new Error(
            'The linked quantities did not match.'
          );
        }
      }

      // A full refresh avoids stale
      // Minimog item indexes and totals.
      window.location.reload();

    } catch (error) {
      console.error(
        '[TypeFase Groups] Update failed:',
        error
      );

      alert(
        'We could not update your customized ' +
        'items. Please refresh your cart ' +
        'and try again.'
      );

      window.location.reload();

    } finally {
      updating = false;

      instance.loading?.finish();
    }
  }

  /* ------------------------------------------
     MINIMOG INTEGRATION
  ------------------------------------------ */

  function install() {
    const CartItems =
      customElements.get('m-cart-items');

    if (!CartItems) {
      return false;
    }

    const prototype = CartItems.prototype;

    if (
      prototype._typefaseUniversalInstalled
    ) {
      return true;
    }

    if (
      typeof prototype.updateQuantity !==
      'function'
    ) {
      return false;
    }

    const original =
      prototype.updateQuantity;

    prototype.updateQuantity = function(
      line,
      quantity,
      name,
      target
    ) {
      if (updating || checking) {
        return;
      }

      const next = Number(quantity);

      if (
        !Number.isSafeInteger(next) ||
        next < 0
      ) {
        return original.call(
          this,
          line,
          quantity,
          name,
          target
        );
      }

      checking = true;

      getCart()
        .then(cart => {
          const selected =
            cart.items[Number(line) - 1];

          // Products without a TypeFase
          // group retain normal Minimog
          // cart functionality.
          if (!groupId(selected)) {
            return original.call(
              this,
              line,
              quantity,
              name,
              target
            );
          }

          // Do not let customers edit an
          // add-on independently.
          if (isAddon(selected)) {
            alert(
              'This customization is linked ' +
              'to its main product. Please ' +
              'change the main product quantity.'
            );

            window.location.reload();
            return;
          }

          const group =
            findGroup(cart, line);

          if (!group) {
            alert(
              'We could not verify the ' +
              'customization group. Please ' +
              'refresh your cart before ' +
              'making changes.'
            );

            window.location.reload();
            return;
          }

          return updateGroup(
            this,
            group,
            next
          );
        })
        .catch(error => {
          console.error(
            '[TypeFase Groups] Lookup failed:',
            error
          );

          alert(
            'Unable to verify the shopping ' +
            'cart. Please refresh and retry.'
          );
        })
        .finally(() => {
          checking = false;
        });
    };

    prototype._typefaseUniversalInstalled =
      true;

    return true;
  }

  /* ------------------------------------------
     INITIALIZE
  ------------------------------------------ */

  function initialize() {
    if (install()) {
      return;
    }

    // Allow for deferred theme scripts.
    let attempts = 0;

    const retry = setInterval(() => {
      attempts += 1;

      if (install() || attempts >= 20) {
        clearInterval(retry);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

})();
